import * as PIXI from 'pixi.js';
import { ColorMatrixFilter } from '@pixi/filter-color-matrix';
import { Config } from '../../video-data/config';
import { ColorAttribute } from '../attributes';
import { rgb2int } from '../../libs/videoData';
import { comparer, observable, reaction } from '../reactivity';
import type { BaseSettings } from '../TimeAwareBase';
import { Filter } from './Filter';

/**
 *  Creates "duotone" effect remapping the color spectrum between two arbitrary colors
 */
export class DuoToneFilter extends Filter {
    override _classType = Config.DUO_TONE_FILTER;
    override pixiFilter: ColorMatrixFilter;

    @ColorAttribute()
    @observable
    accessor firstColor = rgb2int([0, 0, 0]);
    @ColorAttribute()
    @observable
    accessor secondColor = rgb2int([255, 255, 255]);

    constructor(settings: BaseSettings) {
        super(settings);
        this.pixiFilter = new ColorMatrixFilter();

        this.addReaction(
            reaction(
                () => ({ firstColor: this.firstColor, secondColor: this.secondColor }),
                ({ firstColor, secondColor }) => {
                    const first = PIXI.Color.shared.setValue(firstColor).toRgbArray();
                    const second = PIXI.Color.shared.setValue(secondColor).toRgbArray();
                    this.pixiFilter.matrix = [
                        first[0] - second[0],
                        0,
                        0,
                        0,
                        second[0],
                        first[1] - second[1],
                        0,
                        0,
                        0,
                        second[1],
                        first[2] - second[2],
                        0,
                        0,
                        0,
                        second[2],
                        0,
                        0,
                        0,
                        1,
                        0,
                    ];
                },
                { equals: comparer.shallow, name: 'DuoToneFilter.matrix' },
            ),
        );
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['firstColor', 'secondColor']);
    }

    override isNeutral(): boolean {
        return (
            this.firstColor === rgb2int([0, 0, 0]) && this.secondColor === rgb2int([255, 255, 255])
        );
    }
}
